import { PrivacyBlur } from "#components/privacy-blur"
import { useTheme } from "#components/theme-provider"
import { Body, MetricValue, StatLabel, StatValue } from "#components/ui/typography"
import { useCurrencyFormatters } from "#hooks/use-currency-formatters"
import { useI18n } from "#hooks/use-i18n"
import { slideUp } from "#lib/animations"
import type { DashboardAnalyticsResponse } from "@home-finance-manager/shared/types/dashboard"
import { motion } from "framer-motion"
import { CalendarDays, Sparkles, TrendingDown, TrendingUp, Users } from "lucide-react"
import React, { useMemo } from "react"

interface GlobalSummaryCardProps {
  analytics: DashboardAnalyticsResponse | undefined
}

export const GlobalSummaryCard = React.memo<GlobalSummaryCardProps>(({ analytics }) => {
  const { t } = useI18n()
  const { resolvedTheme } = useTheme()
  const { formatCurrency } = useCurrencyFormatters()
  const isDark = resolvedTheme === "dark"

  // Get the most recent month's data (last in the array, as they're sorted chronologically)
  const selectedMonthIndex = useMemo(() => {
    if (!analytics?.totalsByMonth || analytics.totalsByMonth.length === 0) return -1
    return analytics.totalsByMonth.length - 1
  }, [analytics])

  const selectedMonthTotals =
    selectedMonthIndex >= 0 ? analytics?.totalsByMonth?.[selectedMonthIndex] || null : null
  const selectedMonthPartnerContributions =
    selectedMonthIndex >= 0
      ? analytics?.partnerContributionsByMonth?.[selectedMonthIndex] || null
      : null

  // Calculate remaining money for pleasure and saving
  const remainingMoney = useMemo(() => {
    if (!selectedMonthTotals) return 0
    return selectedMonthTotals.totalIncome - selectedMonthTotals.totalExpenses
  }, [selectedMonthTotals])

  // Calculate per-partner remaining money
  const partnerRemainingMoney = useMemo(() => {
    if (!selectedMonthPartnerContributions?.partnerContributions) return []
    return selectedMonthPartnerContributions.partnerContributions.map((contribution) => {
      const netSalary = contribution.netMonthlySalary || 0
      const extraIncome = contribution.extraIncome || 0
      const totalIncome = netSalary + extraIncome
      const totalSpending = contribution.totalSpending || 0
      const remaining = totalIncome - totalSpending
      return {
        partnerId: contribution.partnerId,
        partnerName: contribution.partnerName,
        remaining,
      }
    })
  }, [selectedMonthPartnerContributions])

  // Calculate expense trend vs previous month
  const expenseTrend = useMemo(() => {
    if (!selectedMonthTotals || selectedMonthIndex <= 0) return null
    const previousMonthTotals = analytics?.totalsByMonth?.[selectedMonthIndex - 1]
    if (!previousMonthTotals || previousMonthTotals.totalExpenses === 0) return null

    const change =
      ((selectedMonthTotals.totalExpenses - previousMonthTotals.totalExpenses) /
        previousMonthTotals.totalExpenses) *
      100
    return {
      percentage: change,
      isIncreasing: change > 0,
      previousAmount: previousMonthTotals.totalExpenses,
    }
  }, [selectedMonthTotals, selectedMonthIndex, analytics])

  // Calculate average daily spending
  const averageDailySpending = useMemo(() => {
    if (!selectedMonthTotals) return 0
    const today = new Date()
    const currentDay = today.getDate()
    // Use current day or assume we're looking at a complete month
    const daysElapsed = currentDay
    return daysElapsed > 0 ? selectedMonthTotals.totalExpenses / daysElapsed : 0
  }, [selectedMonthTotals])

  // Calculate savings rate
  const savingsRate = useMemo(() => {
    if (!selectedMonthTotals || selectedMonthTotals.totalIncome === 0) return 0
    return (remainingMoney / selectedMonthTotals.totalIncome) * 100
  }, [remainingMoney, selectedMonthTotals])

  // Get days remaining in month
  const daysRemaining = useMemo(() => {
    const today = new Date()
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()
    return lastDay - today.getDate()
  }, [])

  // Partner colors for indicators
  const partnerColors = ["#3b82f6", "#8b5cf6", "#ef4444", "#f59e0b", "#22c55e"]

  if (!selectedMonthTotals) return null

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={slideUp}
      className="mb-6 relative overflow-hidden rounded-xl border bg-card/50 backdrop-blur-sm shadow-sm dark:shadow-none dark:border-border/50"
    >
      {/* Accent border gradient */}
      <div
        className={`absolute top-0 left-0 right-0 h-1 ${
          isDark
            ? "bg-gradient-to-r from-primary/60 via-primary/40 to-transparent"
            : "bg-gradient-to-r from-primary/80 via-primary/60 to-transparent"
        }`}
      />
      <div className="p-6 sm:p-8">
        {/* Header */}
        <div className="mb-6">
          <StatLabel className="text-sm mb-1">
            {t("components.dashboard.globalSummary.financialSummary")}
          </StatLabel>
          <StatValue className="text-3xl sm:text-4xl">
            <PrivacyBlur>{formatCurrency(selectedMonthTotals.totalExpenses)}</PrivacyBlur>
          </StatValue>
          <Body className="text-xs text-muted-foreground mt-1">
            {t("components.dashboard.globalSummary.totalExpensesThisMonth")}
          </Body>
        </div>

        {/* Three-column layout: Expense Trend, Partner Contributions, Remaining */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 mb-6">
          {/* Expense Trend Section */}
          <div className="flex flex-col gap-3 p-4 rounded-lg bg-muted/30 border border-border/50">
            <div className="flex items-center gap-2">
              <div
                className={`p-2 rounded-lg ${
                  expenseTrend?.isIncreasing
                    ? isDark
                      ? "bg-destructive/10 border border-destructive/20"
                      : "bg-destructive/5 border border-destructive/10"
                    : isDark
                      ? "bg-green-600/10 border border-green-600/20"
                      : "bg-green-600/5 border border-green-600/10"
                }`}
              >
                {expenseTrend?.isIncreasing ? (
                  <TrendingUp className="w-5 h-5 text-destructive" />
                ) : (
                  <TrendingDown className="w-5 h-5 text-green-600" />
                )}
              </div>
              <StatLabel className="text-xs">
                {t("components.dashboard.globalSummary.expenseTrend")}
              </StatLabel>
            </div>
            {expenseTrend ? (
              <div className="flex flex-col gap-1">
                <div
                  className={`text-xl sm:text-2xl font-semibold ${
                    expenseTrend.isIncreasing ? "text-destructive" : "text-green-600"
                  }`}
                >
                  {expenseTrend.isIncreasing ? "+" : ""}
                  {expenseTrend.percentage.toFixed(1)}%
                </div>
                <Body className="text-xs text-muted-foreground">
                  {t("components.dashboard.globalSummary.vsLastMonth")}
                </Body>
                <Body className="text-xs text-muted-foreground">
                  {t("components.dashboard.globalSummary.avg")}{" "}
                  <PrivacyBlur>{formatCurrency(averageDailySpending)}</PrivacyBlur>
                  {t("components.dashboard.globalSummary.perDay")}
                </Body>
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                <Body className="text-xs text-muted-foreground">
                  {t("components.dashboard.globalSummary.noComparisonAvailable")}
                </Body>
                <Body className="text-xs text-muted-foreground">
                  {t("components.dashboard.globalSummary.avg")}{" "}
                  <PrivacyBlur>{formatCurrency(averageDailySpending)}</PrivacyBlur>
                  {t("components.dashboard.globalSummary.perDay")}
                </Body>
              </div>
            )}
          </div>

          {/* Partner Contributions Section */}
          <div className="flex flex-col gap-3 p-4 rounded-lg bg-muted/30 border border-border/50">
            <div className="flex items-center gap-2">
              <div
                className={`p-2 rounded-lg ${
                  isDark
                    ? "bg-blue-500/10 border border-blue-500/20"
                    : "bg-blue-500/5 border border-blue-500/10"
                }`}
              >
                <Users className="w-5 h-5 text-blue-500" />
              </div>
              <StatLabel className="text-xs">
                {t("components.dashboard.globalSummary.partnerContributions")}
              </StatLabel>
            </div>
            <div className="flex-1 min-h-0">
              {selectedMonthPartnerContributions?.partnerContributions &&
              selectedMonthPartnerContributions.partnerContributions.length > 0 ? (
                <div className="space-y-2">
                  {selectedMonthPartnerContributions.partnerContributions.map(
                    (contribution, index) => (
                      <div
                        key={contribution.partnerId}
                        className="flex items-center justify-between text-sm"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: partnerColors[index % partnerColors.length],
                            }}
                          />
                          <span className="text-foreground font-medium truncate">
                            {contribution.partnerName}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                          <span className="text-muted-foreground text-xs">
                            {(contribution.equitablePercentage || 0).toFixed(1)}%
                          </span>
                          <span className="font-semibold text-foreground">
                            <PrivacyBlur>{formatCurrency(contribution.totalSpending)}</PrivacyBlur>
                          </span>
                        </div>
                      </div>
                    )
                  )}
                </div>
              ) : (
                <Body className="text-xs text-muted-foreground">No partner data</Body>
              )}
            </div>
          </div>

          {/* Remaining Money Section */}
          <div className="flex flex-col gap-3 p-4 rounded-lg bg-muted/30 border border-border/50">
            <div className="flex items-center gap-2">
              <div
                className={`p-2 rounded-lg ${
                  remainingMoney >= 0
                    ? isDark
                      ? "bg-green-600/10 border border-green-600/20"
                      : "bg-green-600/5 border border-green-600/10"
                    : isDark
                      ? "bg-destructive/10 border border-destructive/20"
                      : "bg-destructive/5 border border-destructive/10"
                }`}
              >
                <Sparkles
                  className={`w-5 h-5 ${
                    remainingMoney >= 0 ? "text-green-600" : "text-destructive"
                  }`}
                />
              </div>
              <StatLabel className="text-xs">
                {t("components.dashboard.globalSummary.remainingForPleasureAndSaving")}
              </StatLabel>
            </div>
            <div className="flex flex-col gap-1">
              <MetricValue
                className={`text-xl sm:text-2xl ${
                  remainingMoney >= 0 ? "text-green-600" : "text-destructive"
                }`}
              >
                <PrivacyBlur>
                  {remainingMoney >= 0 ? "+" : ""}
                  {formatCurrency(remainingMoney)}
                </PrivacyBlur>
              </MetricValue>
              {selectedMonthTotals.totalIncome > 0 && (
                <>
                  <Body className="text-xs text-muted-foreground">
                    {t("components.dashboard.globalSummary.savingsRate")}: {savingsRate.toFixed(1)}%
                  </Body>
                  {daysRemaining > 0 && (
                    <Body className="text-xs text-muted-foreground flex items-center gap-1">
                      <CalendarDays className="w-3 h-3" />
                      {daysRemaining} {t("components.dashboard.globalSummary.daysRemaining")}
                    </Body>
                  )}
                  {/* Per-partner breakdown */}
                  {partnerRemainingMoney.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-border/30 space-y-1">
                      {partnerRemainingMoney.map((partner, index) => {
                        const partnerColor = partnerColors[index % partnerColors.length]
                        return (
                          <div
                            key={partner.partnerId}
                            className="flex items-center justify-between text-[10px]"
                          >
                            <div className="flex items-center gap-1.5 min-w-0">
                              <div
                                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{
                                  backgroundColor: partnerColor,
                                }}
                              />
                              <span className="text-muted-foreground truncate">
                                {partner.partnerName}
                              </span>
                            </div>
                            <span
                              className={`font-medium flex-shrink-0 ml-2 ${
                                partner.remaining >= 0 ? "text-green-600" : "text-destructive"
                              }`}
                            >
                              <PrivacyBlur>
                                {partner.remaining >= 0 ? "+" : ""}
                                {formatCurrency(partner.remaining)}
                              </PrivacyBlur>
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Partner Income Breakdown Section */}
        {selectedMonthPartnerContributions?.partnerContributions &&
        selectedMonthPartnerContributions.partnerContributions.length > 0 ? (
          <div className="border-t border-border/50 pt-6">
            <StatLabel className="text-sm mb-4">
              {t("components.dashboard.globalSummary.partnerIncomeBreakdown")}
            </StatLabel>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedMonthPartnerContributions.partnerContributions.map((contribution, index) => {
                const partnerColor = partnerColors[index % partnerColors.length]
                const grossSalary = contribution.grossMonthlySalary || 0
                const netSalary = contribution.netMonthlySalary || 0
                const extraIncome = contribution.extraIncome || 0
                const totalIncome = netSalary + extraIncome
                const equitablePercentage = contribution.equitablePercentage || 0

                return (
                  <div
                    key={contribution.partnerId}
                    className="flex flex-col gap-3 p-4 rounded-lg bg-muted/30 border border-border/50 relative overflow-hidden"
                  >
                    {equitablePercentage > 0 && (
                      <div
                        className="absolute top-0 right-0 px-2 py-1 text-[10px] font-semibold rounded-bl-lg"
                        style={{
                          backgroundColor: `${partnerColor}20`,
                          color: partnerColor,
                        }}
                      >
                        {equitablePercentage.toFixed(1)}%{" "}
                        {t("components.dashboard.globalSummary.equitable")}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: partnerColor }}
                      />
                      <StatLabel className="text-xs">{contribution.partnerName}</StatLabel>
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <Body className="text-xs text-muted-foreground">
                          {t("components.dashboard.globalSummary.grossSalary")}
                        </Body>
                        <Body className="text-xs font-medium">
                          <PrivacyBlur>{formatCurrency(grossSalary)}</PrivacyBlur>
                        </Body>
                      </div>
                      <div className="flex justify-between items-center">
                        <Body className="text-xs text-muted-foreground">
                          {t("components.dashboard.globalSummary.netSalary")}
                        </Body>
                        <Body className="text-xs font-medium">
                          <PrivacyBlur>{formatCurrency(netSalary)}</PrivacyBlur>
                        </Body>
                      </div>
                      <div className="flex justify-between items-center">
                        <Body className="text-xs text-muted-foreground">
                          {t("components.dashboard.globalSummary.extraIncome")}
                        </Body>
                        <Body
                          className={`text-xs font-medium ${
                            extraIncome > 0 ? "text-green-600" : ""
                          }`}
                        >
                          <PrivacyBlur>
                            {extraIncome > 0 ? "+" : ""}
                            {formatCurrency(extraIncome)}
                          </PrivacyBlur>
                        </Body>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-border/50">
                        <Body className="text-xs font-semibold">
                          {t("components.dashboard.globalSummary.totalIncome")}
                        </Body>
                        <Body className="text-xs font-semibold text-green-600">
                          <PrivacyBlur>{formatCurrency(totalIncome)}</PrivacyBlur>
                        </Body>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}
      </div>
    </motion.div>
  )
})

GlobalSummaryCard.displayName = "GlobalSummaryCard"
